import { useQuery } from "@powersync/tanstack-react-query"
import type { SyncFailureRow } from "@/lib/powersync/syncFailures"
import type { ErrorClassification } from "@/lib/queryClient/MutationError"

export type SyncFailureErrorInfo = {
  message: string
  code: string | null
  hint: string | null
  details: string | null
  /** HTTP status of the rejected request. Absent on records saved before it was captured. */
  status?: number | null
  /**
   * Set when the row was parked because a record it references failed to
   * upload (`code` is then "FIELDBOOK_PARENT_FAILED"): that record.
   */
  parent?: { table: string; id: string }
  /** With `parent`: the code the server itself rejected the row with. */
  server_code?: string | null
}

/**
 * The code the server rejected the row with, for display and telemetry. For
 * a row parked because of its parent, that's the server's code, not the
 * connector's FIELDBOOK_PARENT_FAILED marker.
 */
export function serverErrorCode(info: SyncFailureErrorInfo): string | null {
  return info.parent ? (info.server_code ?? null) : info.code
}

/**
 * A parsed sync failure record.
 *
 * Generic over `T` — the shape of the entity data stored in `op_data`.
 * Defaults to `Record<string, unknown>` for untyped usage.
 */
export type SyncFailure<T = Record<string, unknown>> = Omit<
  SyncFailureRow,
  "classification"
> & {
  classification: ErrorClassification
  parsedErrorInfo: SyncFailureErrorInfo
  parsedOpData: T
}

function rowToSyncFailure<T = Record<string, unknown>>(
  row: SyncFailureRow,
): SyncFailure<T> {
  return {
    ...row,
    classification: row.classification as ErrorClassification,
    parsedErrorInfo: JSON.parse(row.error_info),
    parsedOpData: JSON.parse(row.op_data),
  }
}

/**
 * Get all sync failures for a specific entity.
 * Used by SyncErrorBanner on detail pages.
 */
export function useSyncFailures(
  entityId: string | undefined,
): SyncFailure[] {
  const { data } = useQuery<SyncFailureRow>({
    queryKey: ["syncFailures", "entity", entityId],
    query: entityId
      ? "SELECT * FROM sync_failures WHERE entity_id = ? ORDER BY failed_at DESC"
      : "SELECT * FROM sync_failures WHERE 0",
    parameters: entityId ? [entityId] : [],
  })

  return (data ?? []).map(rowToSyncFailure)
}

/**
 * Get the sync failure for a specific entity filtered to a specific table.
 *
 * Returns the most recent failure matching both `entityId` and `tableName`,
 * or `null` if none exists. Generic over `T` so callers get typed `parsedOpData`.
 *
 * Used by single-entity hooks to merge failed data into query results.
 */
export function useSyncFailureForEntity<T = Record<string, unknown>>(
  tableName: string,
  entityId: string | undefined,
): SyncFailure<T> | null {
  const { data } = useQuery<SyncFailureRow>({
    queryKey: ["syncFailures", "tableEntity", tableName, entityId],
    query: entityId
      ? "SELECT * FROM sync_failures WHERE target_table = ? AND entity_id = ? ORDER BY failed_at DESC LIMIT 1"
      : "SELECT * FROM sync_failures WHERE 0",
    parameters: entityId ? [tableName, entityId] : [],
  })

  const row = data?.[0]
  return row ? rowToSyncFailure<T>(row) : null
}

/**
 * Get all sync failures for a specific table.
 * Used by list view hooks to merge failed items into results.
 */
export function useSyncFailuresByTable<T = Record<string, unknown>>(
  tableName: string,
): SyncFailure<T>[] {
  const { data } = useQuery<SyncFailureRow>({
    queryKey: ["syncFailures", "table", tableName],
    query:
      "SELECT * FROM sync_failures WHERE target_table = ? ORDER BY failed_at DESC",
    parameters: [tableName],
  })

  return (data ?? []).map(rowToSyncFailure<T>)
}
